import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form944Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 944 Instructions for 2025 Annual Federal Tax Return Guide"
          description="Learn to file IRS Form 944 for small businesses with $1,000 or less in payroll tax liability. E-file with TaxZerone for $6.99 and avoid penalties! E-File now!"
          keywords=""
          canonical="resources/employment-tax/form-944-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 944</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-944-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },

                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 944 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        Form 944 (Employer's Annual Federal Tax Return) is filed
                        with the IRS by eligible small employers whose{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          annual liability{' '}
                        </span>
                        for Social Security, Medicare, and withheld federal
                        income taxes is{' '}
                        <span style={{ fontWeight: 600 }}> $1,000 or less</span>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <Link
                          href="/resources/employment-tax/form-944-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>
                        : Form 944 is filed{' '}
                        <span style={{ fontWeight: 600 }}> annually </span> and
                        is generally due by{' '}
                        <span style={{ fontWeight: 600 }}> January 31</span> for
                        the previous calendar year.
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                        Failure to file or pay on time may result in{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          IRS penalties and interest{' '}
                        </span>{' '}
                        on any unpaid taxes.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/employment-tax/e-file-irs-form-944/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 944
                  </Link>{' '}
                  is an{' '}
                  <span style={{ fontWeight: 600 }}>
                    Employer&apos;s Annual Federal Tax Return
                  </span>{' '}
                  for small employers with{' '}
                  <span style={{ fontWeight: 600 }}>$1,000</span> or less in
                  annual payroll tax liability. It simplifies reporting federal
                  income tax, Social Security, and Medicare taxes by letting you
                  file once a year instead of quarterly (like{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 941
                  </Link>
                  ).
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {' '}
                  Unlike Form 941, which is filed quarterly, Form 944 is filed
                  annually.
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What’s New in Form 944 for 2025?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 944?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#except" sx={{ color: '#0202ff' }}>
                          Exceptions
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When Is Form 944 Due?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where Should Form 944 be Filed?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#must" sx={{ color: '#0202ff' }}>
                          Must You Deposit Your Taxes?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#differ" sx={{ color: '#0202ff' }}>
                          Form 944 vs. Form 941: Key Differences
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to File Employer&apos;s Annual Federal Tax Return:
                          Step-by-Step Guide
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What’s New in Form 944 for 2025?
                </Typography>

                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Social Security Tax:
                    </span>{' '}
                    The rate remains{' '}
                    <span style={{ fontWeight: 600 }}>6.2%</span> for both
                    employee and employer, with a new wage base limit of{' '}
                    <span style={{ fontWeight: 600 }}>$176,100</span>.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}> Medicare Tax:</span> The
                    rate stays at <span style={{ fontWeight: 600 }}>1.45%</span>{' '}
                    for both, with no wage base limit.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Household & Election Workers:
                    </span>{' '}
                    Social Security and Medicare taxes apply to household
                    workers earning{' '}
                    <span style={{ fontWeight: 600 }}>$2,800</span> or more, and
                    election workers earning{' '}
                    <span style={{ fontWeight: 600 }}>$2,400</span> or more.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Direct Deposit for Refunds:
                    </span>{' '}
                    Following{' '}
                    <Link
                      href="https://www.irs.gov/eo14247"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Executive Order 14247
                    </Link>{' '}
                    (March 25, 2025), the IRS now issues Form 944 refunds by
                    direct deposit for faster and more secure payments. You can
                    also apply any overpayment to your next return (see line
                    12b).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Electronic Payments Required:
                    </span>{' '}
                    EO 14247 also mandates electronic payment of any balance due
                    on Form 944. See line 11 instructions for details.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Electronic Return Transcripts:
                    </span>{' '}
                    Form 944{' '}
                    <Link
                      href="https://www.irs.gov/businesstranscript"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      transcripts
                    </Link>{' '}
                    for tax years 2023 and later are now available through your{' '}
                    <span style={{ fontWeight: 600 }}>
                      IRS Business Tax Account
                    </span>{' '}
                    at{' '}
                    <Link
                      href="https://www.irs.gov/BusinessAccount"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS.gov/BusinessAccount
                    </Link>
                    .
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Withholding on qualified tips:
                    </span>{' '}
                    For tax years beginning after 2024 and ending before 2029,
                    certain new deductions come into effect from the law
                    commonly called the <br />
                    <span style={{ fontWeight: 600 }}>
                      “One Big Beautiful Bill Act”
                    </span>
                    .
                  </li>
                  <ul>
                    <li>
                      Qualified tips deduction up to $25,000 (for
                      employees/self-employed) in occupations that are listed by
                      the IRS as customarily and regularly receiving tips on or
                      before December 31, 2024.{' '}
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Qualified overtime compensation:
                    </span>{' '}
                    For tax years beginning after 2024, and ending before 2029,
                    allows individuals to deduction up to $12,500 (or $25,000 if
                    married filing jointly) for certain workers. Employers and
                    other payers must file the information returns (W-2,
                    1099-MISC, etc) and furnish statements to overtime
                    recipients. Transition relief is provided for tax year 2025
                    for reporting requirements.{' '}
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who must file Form 944?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Generally, if the IRS has instructed you to{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-944/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 944
                  </Link>
                  , you must file this form annually instead of submitting{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    quarterly Form 941
                  </Link>
                  . This form reports the following:
                </Typography>
                <Box component="ul" sx={{ mb: 3.5, '& li': { mb: 1 } }}>
                  <li>Wages paid to employees</li>
                  <li>Tips reported by your employees</li>
                  <li>Federal income tax withheld</li>
                  <li>
                    Both the employer and employee portions of Social Security
                    and Medicare taxes
                  </li>
                  <li>Additional Medicare Tax withheld from employees</li>
                  <li>
                    Adjustments for the current year related to Social Security
                    and Medicare taxes, including fractions of cents, sick pay,
                    tips, and group-term life insurance
                  </li>
                  <li>
                    Qualified small business payroll tax credit for increasing
                    research activities.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="except"
                >
                  Exceptions
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Certain employers are not eligible to file Form 944. These
                  include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>
                    <b>Household Employers:</b> If you only employ household
                    workers, Form 944 is not applicable.
                  </li>
                  <li>
                    <b>Agricultural Employers:</b> If you only employ
                    agricultural workers, you are not required to file Form 944.
                  </li>
                  <li>
                    <b>
                      Employers Notified by the IRS to File Quarterly Forms 941:
                    </b>{' '}
                    This applies if you’ve received confirmation from the IRS
                    that your filing requirement has been changed from Form 944
                    to Form 941.
                  </li>
                  <li>
                    <b>Employers Not Notified by the IRS to File Form 944:</b>{' '}
                    If the IRS does not instruct you to file Form 944, do not
                    file it. If you&apos;d prefer to file Form 944 instead of
                    quarterly Forms 941, refer to the section on{' '}
                    <Link
                      href="https://www.irs.gov/pub/irs-pdf/i944.pdf#page=4&zoom=100,0,494"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Requesting To File Form 944 in 2025 Instead of Quarterly
                      Forms 941
                    </Link>
                    .
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="when"
                >
                  When Is Form 944 Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 944 for the 2025 tax year is due by{' '}
                  <span style={{ fontWeight: 600 }}>February 02, 2025</span>.
                  However, if you have fully paid your annual tax liability
                  through timely deposits, you have until{' '}
                  <span style={{ fontWeight: 600 }}>February 10, 2026</span>, to
                  file the form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Ensure that Form 944 is filed only once per calendar year. If
                  you file electronically, avoid submitting a paper
                  version.{' '}
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        E-file Form 944 for 2025 with TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Simplify your annual payroll tax filing. Report wages,
                        taxes, and contributions with ease. Avoid penalties—file
                        on time!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=944instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="where"
                >
                  Where Should Form 944 be Filed?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS encourages you to{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-944/"
                    sx={{ color: '#0202ff' }}
                  >
                    <b>file Form 944 electronically</b>
                  </Link>
                  , as it provides a streamlined method to fulfill your filing
                  requirements. If you choose to file a paper return, the
                  mailing address depends on whether a payment is included with
                  your Form 944. Use the table below to determine the
                  appropriate address for your location.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> Private Delivery Services (PDSs) cannot deliver
                  to P.O. boxes. For PDS deliveries, use the mailing address
                  corresponding to returns filed without payment, as detailed
                  below.
                </Alert>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 200 }}>
                            If your location is...
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 200 }}>
                            Without a payment...
                          </TableCell>
                          <TableCell sx={{ color: '#444', width: 200 }}>
                            With a payment...
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Connecticut, Delaware, District of Columbia,
                            Georgia, Illinois, Indiana, Kentucky, Maine,
                            Maryland, Massachusetts, Michigan, New Hampshire,
                            New Jersey, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            Kansas City,
                            <br />
                            MO 64999-0044
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville,
                            <br />
                            KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                            Louisiana, Minnesota, Mississippi, Missouri,
                            Montana, Nebraska, Nevada, New Mexico, North Dakota,
                            Oklahoma, Oregon, South Dakota, Texas, Utah,
                            Washington, Wyoming
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0044
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            No legal residence or principal place of business in
                            any state, including employers in American Samoa,
                            Guam, the CNMI, the USVI, and Puerto Rico
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 409101
                            <br />
                            Ogden, UT 84409
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            <b> Special filing address</b> for exempt
                            organizations; federal, state, and local
                            governmental entities; and Indian tribal
                            governmental entities, regardless of location
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0044
                          </TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="must"
                >
                  Must You Deposit Your Taxes?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your total taxes after adjustments and nonrefundable
                  credits (Form 944, line 9) are under <b>$2,500</b>, you can
                  pay them directly with your return. To avoid penalties, pay in
                  full and file on time. Depositing the taxes isn’t required,
                  but you can choose to do so if you prefer. If your liability
                  is $2,500 or more, you’ll need to deposit the taxes instead of
                  paying them with your return. Refer to the{' '}
                  <b>
                    <Link href="#federal" sx={{ color: '#0202ff' }}>
                      Federal Tax Deposit Requirements for Form 944 Filers
                    </Link>
                  </b>{' '}
                  table below for more information. Failing to deposit taxes on
                  time may result in penalties and interest.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Keep in mind, that the $2,500 threshold for federal tax
                  deposits is different from the $1,000 threshold for filing
                  Form 944. Even if your business grows during the year, you’ll
                  still file Form 944 but may need to make federal tax deposits.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '22px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Federal Tax Deposit Requirements for Form 944 Filers
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Tax Liability
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 400 }}>
                            Deposit Requirement
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Less than $2,500 for the year
                          </TableCell>
                          <TableCell>
                            No deposit is required. You may pay the tax with
                            your return. If unsure your liability will be under
                            $2,500, but you can still make deposits as outlined
                            below.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $2,500 or more for the year but less than $2,500 for
                            the quarter
                          </TableCell>
                          <TableCell>
                            You can deposit by the last day of the month
                            following the end of a quarter. If your
                            fourth-quarter tax liability is under $2,500, you
                            may pay it with Form 944.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $2,500 or more for the quarter
                          </TableCell>
                          <TableCell>
                            You must make monthly or semiweekly deposits based
                            on your deposit schedule. If your taxes reach
                            $100,000 or more on any given day, you must deposit
                            the tax by the next business day. See section 11 of
                            Pub. 15 for details.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="differ"
                >
                  Form 944 vs. Form 941: Key Differences
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Feature
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Form 944
                          </TableCell>
                          <TableCell sx={{ color: '#444', width: 200 }}>
                            Form 941
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Purpose
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            For small employers with annual tax liabilities of
                            $1,000 or less.
                          </TableCell>
                          <TableCell>
                            For larger employers to report quarterly payroll
                            taxes.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing Frequency
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Filed once a year (Annually)
                          </TableCell>
                          <TableCell>
                            Filed four times a year (Quarterly)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Due Dates
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            February 2 (or February 10 if taxes are fully paid
                            on time)
                          </TableCell>
                          <TableCell>
                            April 30, July 31, October 31, and February 2
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Tax Liability Threshold
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Specifically for businesses with a total payroll tax
                            liability of $1,000 or less annually
                          </TableCell>
                          <TableCell>
                            Designed for businesses with a higher payroll tax
                            liability.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Tax Payment Frequency
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Typically once a year unless liability exceeds
                            $2,500
                          </TableCell>
                          <TableCell>
                            Monthly or semi-weekly, based on total tax liability
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Amended Form
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Corrections are made using Form 944-X.
                          </TableCell>
                          <TableCell>
                            Corrections are made using Form 941-X.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <ul style={{ marginBottom: 24 }}>
                  <li>
                    <b>Key Reminder:</b> Verify with the IRS which form applies
                    to your business before filing.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 87,
                  }}
                  id="how"
                >
                  How to File Employer&apos;s Annual Federal Tax Return:
                  Step-by-Step Guide
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 1: Enter the Entity Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/entity-information-img.webp" />
                  <source srcSet="/assets/images/entity-information-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/entity-information-img.png"
                    loading="lazy"
                    alt="Form 944: Enter the EIN, Name, and complete address"
                    width={650}
                    height={282}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Box component="ul" sx={{ mb: 4 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Employer Identification Number (EIN):</b> Enter your
                    unique EIN in the designated box.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Name:</b> Provide your legal business name.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Trade Name:</b> If your business operates under a
                    different name, enter it here.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Address:</b> Fill in your business address, including
                    street, suite, city, state, ZIP, and foreign address details
                    (if applicable).
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 2: Fill Out Part 1: Annual Tax Return Information
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 1:</b> Enter total wages, tips, and other
                    compensation paid to employees.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-wages-img.webp" />
                  <source srcSet="/assets/images/f944-wages-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-wages-img.png"
                    loading="lazy"
                    alt="Form 944 Line 1: Enter the wages, tips and other compensation"
                    width={658}
                    height={31}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 2:</b> Enter federal income tax withheld.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/944-federal-img.webp" />
                  <source srcSet="/assets/images/944-federal-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/944-federal-img.png"
                    loading="lazy"
                    alt="Form 944 Line 2: federal income tax withheld from wages, tips or other compensation"
                    width={658}
                    height={30}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 3:</b> If no wages are subject to Social Security or
                    Medicare tax, check the box and proceed to Line 5.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-wage-tips-img.webp" />
                  <source srcSet="/assets/images/f944-wage-tips-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-wage-tips-img.png"
                    loading="lazy"
                    alt="Form 944 Line 3: If no wages are subject to Social Security or Medicare tax, check the box and go to line 5."
                    width={658}
                    height={23}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Lines 4a–4d:</b> Calculate Social Security and Medicare
                    taxes based on taxable wages and tips, then total them in
                    Line 4e.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-taxable-img.webp" />
                  <source srcSet="/assets/images/f944-taxable-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-taxable-img.png"
                    loading="lazy"
                    alt="Form 944 Line 4: Enter the FICA wages and tips"
                    width={658}
                    height={186}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 5:</b> Add Line 2 and Line 4e for total taxes before
                    adjustments.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-total-img.webp" />
                  <source srcSet="/assets/images/f944-total-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-total-img.png"
                    loading="lazy"
                    alt="Form 944 Line 5: Add Line 2 and Line 4e for total taxes before adjustments."
                    width={658}
                    height={29}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 6:</b> Adjust taxes for prior year corrections or
                    other adjustments.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-current-img.webp" />
                  <source srcSet="/assets/images/f944-current-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-current-img.png"
                    loading="lazy"
                    alt="Form 944 Line 6: Adjust taxes for prior year corrections or other adjustments."
                    width={658}
                    height={29}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 7:</b> Combine Lines 5 and 6 for total taxes after
                    adjustments.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-total-tax-img.webp" />
                  <source srcSet="/assets/images/f944-total-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-total-tax-img.png"
                    loading="lazy"
                    alt="Form 944 Line 7: Combine Lines 5 and 6 for total taxes after adjustments."
                    width={658}
                    height={29}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 8:</b> Enter qualified small business payroll tax
                    credit (if applicable, attach Form 8974).
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-qualified-img.webp" />
                  <source srcSet="/assets/images/f944-qualified-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-qualified-img.png"
                    loading="lazy"
                    alt="Form 944 Line 8: Enter qualified small business payroll tax credit (if applicable, attach Form 8974)."
                    width={658}
                    height={28}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 9:</b> Subtract Line 8 from Line 7 for total taxes
                    after credits.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-credits-img.webp" />
                  <source srcSet="/assets/images/f944-credits-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-credits-img.png"
                    loading="lazy"
                    alt="Form 944 Line 9: Subtract Line 8 from Line 7 for total taxes after credits."
                    width={658}
                    height={28}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 10:</b> Enter total deposits for the year, including
                    prior overpayments applied.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-deposits-img.webp" />
                  <source srcSet="/assets/images/f944-deposits-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-deposits-img.png"
                    loading="lazy"
                    alt="Form 944 Line 10: Enter total deposits for the year, including prior overpayments applied. Enter total deposits for the year, including prior overpayments applied."
                    width={658}
                    height={28}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Line 11:</b> If taxes (Line 9) exceed deposits (Line 10),
                    enter the balance due.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-balance-due-img.webp" />
                  <source srcSet="/assets/images/f944-balance-due-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-balance-due-img.png"
                    loading="lazy"
                    alt="Form 944 Line 11: If taxes (Line 9) exceed deposits (Line 10), enter the balance due."
                    width={658}
                    height={28}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Line 12a:</span> If Line
                    10 is greater than Line 9, enter the difference on line 12a.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Line 12b:</span> If you
                    have an overpayment, select whether you want the amount{' '}
                    <span style={{ fontWeight: 600 }}>
                      applied to your next return
                    </span>{' '}
                    or as a <span style={{ fontWeight: 600 }}>refund</span>.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Line 12c:</span> Enter the
                    routing number
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Line 12d:</span> Check the
                    account type – checking or savings
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Line 12e:</span> Enter the
                    account number
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-overpayment-img.webp" />
                  <source srcSet="/assets/images/f944-overpayment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-overpayment-img.png"
                    loading="lazy"
                    alt="Form 944 Line 12a to 12e: Enter the overpayment refund details"
                    width={760}
                    height={86}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 3: Complete Part 2: Deposit Schedule and Tax Liability
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    If Line 9 is less than $2,500, Go to Part 3.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If Line 9 is $2,500 or more, enter your tax liability for
                    each month.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-check-one-img.webp" />
                  <source srcSet="/assets/images/f944-check-one-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-check-one-img.png"
                    loading="lazy"
                    alt="Form 944 Line 13: Enter the tax liability of each month.  Line 13m should be equal to Line 9."
                    width={658}
                    height={220}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 4: Complete Part 3: Business Changes
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Indicate if your business closed or stopped paying wages
                  during the year, and provide the final payroll date.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f944-paying-wages-img.webp" />
                  <source srcSet="/assets/images/f944-paying-wages-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-paying-wages-img.png"
                    loading="lazy"
                    alt="Form 944 Line 14: If your business closed or stopped paying wages during the year, provide the final payroll date."
                    width={658}
                    height={70}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 5: Complete Part 4: Third-Party Designee
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Indicate whether you allow a third party (such as a tax
                  preparer) to discuss this return with the IRS, and provide
                  their information if applicable.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f944-discuss-img.webp" />
                  <source srcSet="/assets/images/f944-discuss-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-discuss-img.png"
                    loading="lazy"
                    alt="Form 944 Part 4: Enter the third-party designee’s name, phone number, and PIN. "
                    width={658}
                    height={101}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 6: Sign and Date the Form
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    The business owner or authorized person must sign, date, and
                    provide a daytime phone number.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If a paid preparer completes the form, they must also sign
                    and provide their details.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f944-sign-img.webp" />
                  <source srcSet="/assets/images/f944-sign-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f944-sign-img.png"
                    loading="lazy"
                    alt="Form 944 Part 5: Enter your Sign, date, and best daytime to make a call."
                    width={656}
                    height={130}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                    fontStyle: 'italic',
                  }}
                >
                  For additional details, refer to the{' '}
                  <Link
                    href="https://www.irs.gov/instructions/i944"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Instructions for Form 944
                  </Link>
                </Typography>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent={{ xs: 'center', md: 'space-between' }}
                  alignItems="center"
                  spacing={2}
                  sx={{
                    py: 2,
                    px: { xs: 1, md: 2 },
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #ccc',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      textAlign: { xs: 'center', md: 'left' },
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '19px !important',
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      Simplify Your Signature Process with TaxZerone!{' '}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0 }}>
                      With TaxZerone, you can choose to e-sign your return using
                      Form 8453-EMP or your IRS-assigned{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-94x-pin-request/"
                        sx={{ color: '#0202ff' }}
                      >
                        94X Online Signature PIN
                      </Link>
                      .
                    </Typography>
                  </Box>
                  <Box sx={{ width: 180 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=944instructions`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 944 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File{' '}
              <Link
                href="/employment-tax/e-file-irs-form-944/"
                sx={{ color: '#0202ff' }}
              >
                IRS Form 944
              </Link>{' '}
              quickly with TaxZerone for just <b>$6.99 per return</b>. Simply
              enter
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              the required details, make your payment, sign, and submit your
              return to the IRS.
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>
              TaxZerone also supports payments through{' '}
              <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                EFTPS
              </Link>
              .
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              The entire filing process takes under 5 minutes, and you&apos;ll
              receive email updates{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>
              once the IRS processes your return.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=944instructions`}
              >
                E-File Form 944 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form944Instructions;
